import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { FiscalBatchData } from "@/hooks/useProductsFiscal";
import { TaxRule } from "@/hooks/useTaxRules";
import { ORIGENS, CFOP_OPTIONS, UNIDADES_COMERCIAIS, NCM_SUGERIDOS } from "@/lib/fiscal-constants";

interface ProductFiscalBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  taxRules: TaxRule[];
  onSave: (data: FiscalBatchData) => Promise<boolean>;
}

export function ProductFiscalBatchDialog({
  open,
  onOpenChange,
  selectedCount,
  taxRules,
  onSave,
}: ProductFiscalBatchDialogProps) {
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState({
    applyNcm: false,
    applyCest: false,
    applyCfop: false,
    applyOrigem: false,
    applyUnidade: false,
    applyTaxRule: false,
  });
  const [values, setValues] = useState({
    ncm: "",
    cest: "",
    cfop: "5102",
    origem: "0",
    unidade_comercial: "UN",
    tax_rule_id: "__none__",
  });

  const handleSave = async () => {
    const data: FiscalBatchData = {};

    if (fields.applyNcm) data.ncm = values.ncm || null;
    if (fields.applyCest) data.cest = values.cest || null;
    if (fields.applyCfop) data.cfop = values.cfop || null;
    if (fields.applyOrigem) data.origem = values.origem || null;
    if (fields.applyUnidade) data.unidade_comercial = values.unidade_comercial || null;
    if (fields.applyTaxRule) {
      data.tax_rule_id = values.tax_rule_id === "__none__" ? null : values.tax_rule_id;
    }

    setSaving(true);
    const success = await onSave(data);
    setSaving(false);

    if (success) {
      // Reset form
      setFields({
        applyNcm: false,
        applyCest: false,
        applyCfop: false,
        applyOrigem: false,
        applyUnidade: false,
        applyTaxRule: false,
      });
      setValues({
        ncm: "",
        cest: "",
        cfop: "5102",
        origem: "0",
        unidade_comercial: "UN",
        tax_rule_id: "__none__",
      });
      onOpenChange(false);
    }
  };

  const hasAnyField = Object.values(fields).some(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar Dados Fiscais em Lote</DialogTitle>
          <DialogDescription>
            {selectedCount} produto(s) selecionado(s)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Apenas os campos marcados serão alterados nos produtos selecionados.
            </AlertDescription>
          </Alert>

          {/* NCM */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="applyNcm"
              checked={fields.applyNcm}
              onCheckedChange={(checked) => setFields({ ...fields, applyNcm: !!checked })}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="applyNcm" className="cursor-pointer">Aplicar NCM</Label>
              {fields.applyNcm && (
                <>
                  <Select
                    value={values.ncm}
                    onValueChange={(value) => setValues({ ...values, ncm: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um NCM..." />
                    </SelectTrigger>
                    <SelectContent>
                      {NCM_SUGERIDOS.map((ncm) => (
                        <SelectItem key={ncm.value} value={ncm.value}>
                          {ncm.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={values.ncm}
                    onChange={(e) => setValues({ ...values, ncm: e.target.value.replace(/\D/g, "") })}
                    placeholder="Ou digite o NCM..."
                    maxLength={8}
                  />
                </>
              )}
            </div>
          </div>

          {/* CEST */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="applyCest"
              checked={fields.applyCest}
              onCheckedChange={(checked) => setFields({ ...fields, applyCest: !!checked })}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="applyCest" className="cursor-pointer">Aplicar CEST</Label>
              {fields.applyCest && (
                <Input
                  value={values.cest}
                  onChange={(e) => setValues({ ...values, cest: e.target.value.replace(/\D/g, "") })}
                  placeholder="Ex: 1705800"
                  maxLength={7}
                />
              )}
            </div>
          </div>

          {/* CFOP */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="applyCfop"
              checked={fields.applyCfop}
              onCheckedChange={(checked) => setFields({ ...fields, applyCfop: !!checked })}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="applyCfop" className="cursor-pointer">Aplicar CFOP</Label>
              {fields.applyCfop && (
                <Select
                  value={values.cfop}
                  onValueChange={(value) => setValues({ ...values, cfop: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CFOP_OPTIONS.map((cfop) => (
                      <SelectItem key={cfop.value} value={cfop.value}>
                        {cfop.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Origem */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="applyOrigem"
              checked={fields.applyOrigem}
              onCheckedChange={(checked) => setFields({ ...fields, applyOrigem: !!checked })}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="applyOrigem" className="cursor-pointer">Aplicar Origem</Label>
              {fields.applyOrigem && (
                <Select
                  value={values.origem}
                  onValueChange={(value) => setValues({ ...values, origem: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ORIGENS.map((origem) => (
                      <SelectItem key={origem.value} value={origem.value}>
                        {origem.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Unidade Comercial */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="applyUnidade"
              checked={fields.applyUnidade}
              onCheckedChange={(checked) => setFields({ ...fields, applyUnidade: !!checked })}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="applyUnidade" className="cursor-pointer">Aplicar Unidade Comercial</Label>
              {fields.applyUnidade && (
                <Select
                  value={values.unidade_comercial}
                  onValueChange={(value) => setValues({ ...values, unidade_comercial: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIDADES_COMERCIAIS.map((un) => (
                      <SelectItem key={un.value} value={un.value}>
                        {un.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Regra Tributária */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="applyTaxRule"
              checked={fields.applyTaxRule}
              onCheckedChange={(checked) => setFields({ ...fields, applyTaxRule: !!checked })}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="applyTaxRule" className="cursor-pointer">Aplicar Regra Tributária</Label>
              {fields.applyTaxRule && (
                <Select
                  value={values.tax_rule_id}
                  onValueChange={(value) => setValues({ ...values, tax_rule_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma regra..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhuma (remover vínculo)</SelectItem>
                    {taxRules.map((rule) => (
                      <SelectItem key={rule.id} value={rule.id}>
                        {rule.name} ({rule.ncm})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasAnyField}>
            {saving ? "Aplicando..." : `Aplicar a ${selectedCount} Produto(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
